import { TabContext, TabList } from '@mui/lab';
import { Box, MenuItem, Skeleton, Tab } from '@mui/material';
import { Dispatch, FC, MouseEvent, SetStateAction, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectBranchesForDeploy,
    selectBranchesForDeployCount,
    selectGetBranchesForDeployErrorInfo,
    selectGetBranchesForDeployStatus,
} from '@pages/stands/store/selectors';
import {
    getBranchesForDeployRequest,
    getBranchesForDeployReset,
    resetArrayBranchesForDeploy,
} from '@pages/stands/store';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { BranchFilter, StandBranch } from '@pages/stands/types';
import { RequestStatusEnum } from '@shared/types';
import { usePrevious } from '@shared/hooks/usePrevious';
import { useListState } from '@shared/hooks/useListState';
import HelperText from '@shared/components/others/HelperText/HelperText';
import useHandleScroll from '@shared/hooks/useHandleScroll';
import UtmsSearchTextField from '@shared/components/ui/UtmsSearchTextField';

interface BranchSelectorMenuProps {
    onChange: (branch: StandBranch) => void;
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

const tabStyle = { p: '9px 16px', fontWeight: 600, fontSize: 14, minWidth: 0, minHeight: 42 };

const SKELETONS_COUNT = 6;

const BranchSelectorMenu: FC<BranchSelectorMenuProps> = ({ open, onChange, setOpen }) => {
    const { t } = useTranslation();
    const [branchesFilter, setBranchesFilter] = useState<BranchFilter>('all');
    const { pagination, setPagination, globalFilter, setGlobalFilter } = useListState(
        'branchesForDeploy',
        false,
        getBranchesForDeployRequest,
        { disableUrlParams: true, branchesFilter },
    );
    const isResettingRef = useRef(false);
    const prevGlobalFilter = usePrevious(globalFilter);
    const getBranchesForDeployStatus = useSelector(selectGetBranchesForDeployStatus);
    const dispatch = useDispatch();
    const branchesForDeploy = useSelector(selectBranchesForDeploy);
    const prevBranchesFilter = usePrevious(branchesFilter);
    const branchesForDeployCount = useSelector(selectBranchesForDeployCount);
    const isLoadingBranchesForDeploy = getBranchesForDeployStatus === RequestStatusEnum.Loading;
    const { pageIndex, pageSize } = pagination;
    const allBranchesFetched = pageIndex * pageSize >= branchesForDeployCount;
    const handleScroll = useHandleScroll({
        isLoading: isLoadingBranchesForDeploy,
        setPagination,
        allItemsFetched: allBranchesFetched,
    });

    const handleChangeTab = (_e: SyntheticEvent, newValue: BranchFilter) => {
        setBranchesFilter(newValue);
    };

    const handleSelectBranch = (branch: StandBranch) => (_e: MouseEvent<HTMLElement>) => {
        onChange(branch);
        setOpen(false);
    };

    useEffect(() => {
        if (
            (branchesFilter !== prevBranchesFilter && prevBranchesFilter) ||
            (globalFilter !== '' && globalFilter !== prevGlobalFilter) || // global filter changed
            (globalFilter === '' && prevGlobalFilter !== undefined && globalFilter !== prevGlobalFilter) // global filter was set to empty string
        ) {
            isResettingRef.current = true;
            dispatch(resetArrayBranchesForDeploy());
        }
    }, [branchesFilter, dispatch, globalFilter, prevBranchesFilter, prevGlobalFilter]);

    useEffect(() => {
        if (!open) {
            isResettingRef.current = true;
        }
    }, [open]);

    useEffect(() => {
        if (!isLoadingBranchesForDeploy) {
            isResettingRef.current = false;
        }
    }, [isLoadingBranchesForDeploy]);

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TabContext value={branchesFilter}>
                    <TabList sx={{ minHeight: 42 }} onChange={handleChangeTab}>
                        <Tab sx={tabStyle} label={t('all')} value="all" />
                        <Tab sx={tabStyle} label={t('task')} value="task" />
                        <Tab sx={tabStyle} label={t('release')} value="release" />
                        <Tab sx={tabStyle} label={t('other')} value="other" />
                    </TabList>
                </TabContext>
                <UtmsSearchTextField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                <Box sx={{ maxHeight: 275, overflow: 'auto' }} onScroll={handleScroll}>
                    {branchesForDeploy.map((branch: StandBranch) => (
                        <MenuItem key={branch} value={branch} onClick={handleSelectBranch(branch)}>
                            {branch}
                        </MenuItem>
                    ))}
                    {isLoadingBranchesForDeploy &&
                        [...Array(SKELETONS_COUNT)].map((_, i) => (
                            <MenuItem key={i}>
                                <Skeleton animation="wave" variant="text" width={'100%'} />
                            </MenuItem>
                        ))}
                    {getBranchesForDeployStatus === RequestStatusEnum.Success && !branchesForDeploy.length && (
                        <HelperText sx={{ fontSize: '0.86rem' /* fontSize from mui from MenuItem */ }}>
                            {t('no_record_to_display')}...
                        </HelperText>
                    )}
                </Box>
            </Box>

            {getBranchesForDeployStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('branch')}
                    action={t('fetching')}
                    resetActionCallback={getBranchesForDeployReset}
                    errorSelector={selectGetBranchesForDeployErrorInfo}
                />
            )}
        </>
    );
};

export default BranchSelectorMenu;
